import { View, Text, StyleSheet } from "@react-pdf/renderer";
import { getSportLabel } from "@/lib/sport-defaults";
import {
  verdictLabel,
  type CityReportStation,
} from "@/components/city-review/types";
import { palette, verdictStatus } from "./styles";

interface StationsSummaryTableProps {
  stations: CityReportStation[];
}

const COLS = {
  label: "15%",
  address: "40%",
  verdict: "15%",
  sports: "30%",
} as const;

const styles = StyleSheet.create({
  table: {
    flexDirection: "column",
    borderRadius: 4,
    overflow: "hidden",
    borderWidth: 0.5,
    borderStyle: "solid",
    borderColor: palette.border,
  },
  headerRow: {
    flexDirection: "row",
    backgroundColor: palette.brand,
    paddingVertical: palette.spacing.sm,
    paddingHorizontal: palette.spacing.sm,
  },
  headerCell: {
    fontSize: palette.type.small,
    fontFamily: "Helvetica-Bold",
    color: palette.white,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  bodyRow: {
    flexDirection: "row",
    paddingVertical: palette.spacing.sm,
    paddingHorizontal: palette.spacing.sm,
    minHeight: 14,
    alignItems: "center",
  },
  bodyRowAlt: {
    backgroundColor: palette.softBg,
  },
  cell: {
    fontSize: palette.type.small,
    color: palette.text,
    paddingRight: palette.spacing.sm,
  },
  muted: {
    color: palette.muted,
  },
  pill: {
    alignSelf: "flex-start",
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 999,
    fontSize: palette.type.tiny,
    fontFamily: "Helvetica-Bold",
  },
});

function sportsSummary(station: CityReportStation): string {
  const labels = station.lockers
    .map((l) => (l.sport_type ? getSportLabel(l.sport_type) : null))
    .filter((s): s is string => Boolean(s));
  if (labels.length === 0) return "—";
  return labels.join(", ");
}

export function StationsSummaryTable({ stations }: StationsSummaryTableProps) {
  return (
    <View style={styles.table}>
      <View style={styles.headerRow}>
        <Text style={[styles.headerCell, { width: COLS.label }]}>Label</Text>
        <Text style={[styles.headerCell, { width: COLS.address }]}>
          Address
        </Text>
        <Text style={[styles.headerCell, { width: COLS.verdict }]}>
          Verdict
        </Text>
        <Text style={[styles.headerCell, { width: COLS.sports }]}>Sports</Text>
      </View>
      {stations.map((station, idx) => {
        const status = verdictStatus(station.city_verdict);
        const colors = palette.status[status];
        const isAlt = idx % 2 === 1;
        return (
          <View
            key={station.id}
            style={[styles.bodyRow, isAlt ? styles.bodyRowAlt : {}]}
          >
            <Text style={[styles.cell, { width: COLS.label }]}>
              {station.label}
            </Text>
            <Text
              style={[
                styles.cell,
                styles.muted,
                { width: COLS.address },
              ]}
            >
              {station.address ?? "—"}
            </Text>
            <View style={{ width: COLS.verdict }}>
              <Text
                style={[
                  styles.pill,
                  {
                    backgroundColor: colors.soft,
                    color: colors.solid,
                  },
                ]}
              >
                {verdictLabel(station.city_verdict)}
              </Text>
            </View>
            <Text style={[styles.cell, { width: COLS.sports }]}>
              {sportsSummary(station)}
            </Text>
          </View>
        );
      })}
    </View>
  );
}
